import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth-provider";
import { Link, useNavigate } from "react-router-dom";
import {
  Mail,
  Lock,
  User,
  ArrowRight,
  CheckCircle2,
  MailCheck,
} from "lucide-react";
import {
  AuthCard,
  AuthInput,
  PasswordField,
  AuthButton,
} from "@/components/auth/auth-form";
import { Badge } from "@/components/ui/badge";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const nameValid = name.trim().length >= 2;
  const emailValid = EMAIL_RE.test(email);
  const passwordsMatch =
    password.length > 0 && confirm.length > 0 && password === confirm;
  const passwordLong = password.length >= 8;
  const canSubmit = nameValid && emailValid && passwordLong && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Couldn't create that account.");
        setLoading(false);
        return;
      }
      setSuccess(true);
    } catch {
      setError("Network error — please try again.");
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthCard
        eyebrow="Almost there"
        title="Check your email"
        subtitle={
          <>
            We sent a verification link to{" "}
            <span className="font-semibold text-foreground">{email}</span>. Click it to
            activate your account.
          </>
        }
        footer={
          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              Back to sign in <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        }
      >
        <div className="flex flex-col items-center text-center py-4">
          <div className="relative mb-5">
            <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-xl" />
            <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-500/30">
              <MailCheck className="h-8 w-8 text-emerald-400" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
            Didn't get the email? Check your spam folder, or{" "}
            <button
              type="button"
              onClick={() => setSuccess(false)}
              className="text-primary font-semibold hover:underline"
            >
              try another address
            </button>
            .
          </p>
        </div>
      </AuthCard>
    );
  }

  // Subtle string subtitle for TS happiness
  const subtitleText = "Save locations, sync preferences, and unlock the briefing.";

  return (
    <AuthCard
      eyebrow="Create account"
      title="Make Vertali your weather"
      subtitle={subtitleText}
      badge={
        <Badge variant="outline" size="sm" className="!gap-1.5">
          <CheckCircle2 className="h-2.5 w-2.5 text-emerald-400" />
          Free, no card
        </Badge>
      }
      footer={
        <p className="text-center text-xs text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="text-primary font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthInput
          label="Name"
          autoComplete="name"
          required
          icon={<User className="h-4 w-4" />}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          validationState={
            name.length === 0 ? "idle" : nameValid ? "valid" : "invalid"
          }
        />

        <AuthInput
          label="Email"
          type="email"
          autoComplete="email"
          required
          icon={<Mail className="h-4 w-4" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          validationState={
            email.length === 0 ? "idle" : emailValid ? "valid" : "invalid"
          }
        />

        <PasswordField
          label="Password"
          autoComplete="new-password"
          required
          icon={<Lock className="h-4 w-4" />}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          showStrength
        />

        <PasswordField
          label="Confirm password"
          autoComplete="new-password"
          required
          icon={<Lock className="h-4 w-4" />}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Repeat your password"
          matchValue={password}
        />

        <RequirementsRow
          requirements={[
            { label: "8+ characters", met: passwordLong },
            { label: "Passwords match", met: passwordsMatch },
            { label: "Looks like an email", met: emailValid },
            { label: "Real name", met: nameValid },
          ]}
        />

        {error && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3.5 py-2.5 text-xs text-rose-300">
            {error}
          </div>
        )}

        <AuthButton type="submit" loading={loading} disabled={!canSubmit}>
          Create account <ArrowRight className="h-4 w-4" />
        </AuthButton>

        <p className="text-[11px] text-center text-muted-foreground leading-relaxed">
          By creating an account you agree to our terms and acknowledge that we don't sell
          your data.
        </p>
      </form>
    </AuthCard>
  );
}

function RequirementsRow({
  requirements,
}: {
  requirements: { label: string; met: boolean }[];
}) {
  const allMet = useMemo(() => requirements.every((r) => r.met), [requirements]);
  return (
    <div className="rounded-xl border border-border/40 bg-background/30 px-3 py-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
          Account checklist
        </span>
        <span
          className={
            "text-[10px] font-bold " +
            (allMet ? "text-emerald-400" : "text-amber-400")
          }
        >
          {requirements.filter((r) => r.met).length}/{requirements.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {requirements.map((r) => (
          <span
            key={r.label}
            className={
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors " +
              (r.met
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                : "border-border/50 bg-background/30 text-muted-foreground")
            }
          >
            <span
              className={
                "inline-block h-1.5 w-1.5 rounded-full " +
                (r.met ? "bg-emerald-400" : "bg-muted-foreground/40")
              }
            />
            {r.label}
          </span>
        ))}
      </div>
    </div>
  );
}
